/* Full-screen weekly timetable view for mobile teacher PWA.
 * Route: /m/teacher/timetable
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mobileTeacherApi } from '../../../lib/api';
import { ArrowLeft, Clock, MapPin, ChalkboardTeacher, CalendarBlank } from '@phosphor-icons/react';

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const DAYS_SHORT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const TeacherTimetable = () => {
  const navigate = useNavigate();
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const todayIdx = useMemo(() => (new Date().getDay() + 6) % 7, []);
  const [selectedDay, setSelectedDay] = useState(todayIdx);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await mobileTeacherApi.timetable();
        setGrouped(data?.grouped_by_day || {});
      } finally { setLoading(false); }
    })();
  }, []);

  const daySessions = grouped[selectedDay] || [];
  const totalSessions = Object.values(grouped).reduce((a, arr) => a + arr.length, 0);

  return (
    <div className="pb-8" data-testid="teacher-timetable">
      {/* Sub-header */}
      <div className="bg-white sticky top-0 z-20 border-b border-[#E2E8F0] px-3 py-2.5 flex items-center gap-2">
        <button
          onClick={() => navigate('/m/teacher/home')}
          className="p-1.5 hover:bg-[#F1F5F9] rounded"
          data-testid="timetable-back"
        >
          <ArrowLeft size={18} className="text-[#64748B]" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-outfit font-bold text-sm">Emploi du temps</div>
          <div className="text-[10px] text-[#64748B]">
            {totalSessions} séance{totalSessions > 1 ? 's' : ''} sur la semaine
          </div>
        </div>
      </div>

      {/* Days scroller */}
      <div className="bg-white border-b border-[#E2E8F0] sticky z-10" style={{ top: 47 }}>
        <div className="grid grid-cols-7 gap-0">
          {DAYS_SHORT.map((short, i) => {
            const isSelected = selectedDay === i;
            const isToday = i === todayIdx;
            const dayCount = (grouped[i] || []).length;
            return (
              <button
                key={i}
                onClick={() => setSelectedDay(i)}
                data-testid={`timetable-day-${i}`}
                className={`py-2.5 flex flex-col items-center relative transition-colors ${
                  isSelected ? 'text-[#0052FF]' : 'text-[#64748B] hover:bg-[#F8FAFC]'
                }`}
              >
                <span className={`text-xs font-medium ${isToday ? 'text-[#10B981]' : ''}`}>
                  {short}
                </span>
                <span className={`text-[10px] mt-0.5 ${isSelected ? 'font-bold' : ''}`}>
                  {dayCount || '·'}
                </span>
                {isSelected && (
                  <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#0052FF] rounded-full" />
                )}
                {isToday && (
                  <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-[#10B981]" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected day sessions */}
      <div className="p-3">
        <h2 className="text-xs uppercase text-[#64748B] font-medium mb-2 flex items-center gap-1.5">
          <CalendarBlank size={12} weight="duotone" /> {DAYS[selectedDay]}
          {selectedDay === todayIdx && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#DCFCE7] text-[#15803D] text-[9px] font-bold">
              AUJOURD&apos;HUI
            </span>
          )}
        </h2>

        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0052FF] mx-auto" />
          </div>
        ) : daySessions.length === 0 ? (
          <div className="text-center py-8 text-[#94A3B8]">
            <CalendarBlank size={32} weight="duotone" className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">Aucune séance ce jour</p>
          </div>
        ) : (
          <ul className="space-y-2.5">
            {daySessions.map((s, i) => (
              <li
                key={i}
                className="bg-white rounded-xl border border-[#E2E8F0] p-3 flex gap-3 shadow-sm"
                data-testid={`timetable-session-${selectedDay}-${i}`}
              >
                <div className="flex flex-col items-center justify-center min-w-[56px] bg-[#EFF6FF] rounded-lg p-2">
                  <div className="text-[10px] uppercase text-[#0052FF] font-medium">Début</div>
                  <div className="font-outfit font-bold text-sm text-[#0052FF]">{s.start_time}</div>
                  <div className="text-[10px] text-[#64748B] mt-1">→ {s.end_time}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-outfit font-bold text-[15px] truncate">
                    {s.subject || 'Cours'}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs text-[#64748B]">
                    <span className="flex items-center gap-1">
                      <ChalkboardTeacher size={11} /> {s.class_name || '—'}
                    </span>
                    {s.room && (
                      <span className="flex items-center gap-1">
                        <MapPin size={11} /> {s.room}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock size={11} />
                      {(() => {
                        const [h1, m1] = (s.start_time || '00:00').split(':').map(Number);
                        const [h2, m2] = (s.end_time || '00:00').split(':').map(Number);
                        const min = (h2 * 60 + m2) - (h1 * 60 + m1);
                        return `${min} min`;
                      })()}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Week overview mini-chart */}
      {!loading && totalSessions > 0 && (
        <div className="px-3 mt-4">
          <h3 className="text-xs uppercase text-[#64748B] font-medium mb-2">Charge de la semaine</h3>
          <div className="grid grid-cols-7 gap-1 bg-white rounded-xl border border-[#E2E8F0] p-3">
            {DAYS_SHORT.map((short, i) => {
              const cnt = (grouped[i] || []).length;
              const maxCnt = Math.max(1, ...Object.values(grouped).map(a => a.length));
              const h = 8 + (cnt / maxCnt) * 40;
              return (
                <div key={i} className="flex flex-col items-center gap-1">
                  <div
                    className={`w-full rounded ${cnt === 0 ? 'bg-[#F1F5F9]' : 'bg-[#0052FF]'}`}
                    style={{ height: `${h}px`, opacity: cnt === 0 ? 0.5 : 0.4 + (cnt / maxCnt) * 0.6 }}
                    title={`${cnt} séance${cnt > 1 ? 's' : ''}`}
                  />
                  <span className={`text-[9px] ${i === todayIdx ? 'text-[#10B981] font-bold' : 'text-[#64748B]'}`}>
                    {short}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherTimetable;
